import {NgModule, Optional, SkipSelf, APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA} from '@angular/core';
import { CommonModule } from '@angular/common';
import {WindowPropertyInitializer} from "./services/initialization/window-property.initializer";
import {LegacyInitializer} from "./services/initialization/legacy.initializer";
import {SharedModule} from "../shared/shared.module";
import {SettingsInitializer} from "./services/settings/settings.initializer";
import {PortalBaseComponent} from "./components/portal-base/portal-base.component";
import { KnockoutFieldComponent } from './components/knockout-field/knockout-field.component';
import { MonacoEditorComponent } from './components/monaco-editor/monaco-editor.component';
import {MonacoEditorModule} from "ngx-monaco-editor-v2";
import {FeatureFlagsInitializer} from "./services/feature-flag/feature-flags.initializer";
import {FeatureFlagComponent} from "./components/feature-flag/feature-flag.component";
import {FeatureFlagListComponent} from "./components/feature-flag-list/feature-flag-list.component";
import { FeatureOnDirective } from './services/feature-flag/feature-on.directive';
import {FeatureOffDirective} from "./services/feature-flag/feature-off.directive";
import {FeatureFlagCanLoadGuard} from "./services/feature-flag/feature-flag-can-load-guard.router";
import {FeatureFlagCanActivateGuard} from "./services/feature-flag/feature-flag-can-activate-guard.router";
import {FeatureFlagCanActivateChildGuard} from "./services/feature-flag/feature-flag-can-activate-child-guard.router";
import {FieldService} from "./services/field/field.service";
import { FeatureOnComponent } from './components/feature-on/feature-on.component';
import { FeatureOffComponent } from './components/feature-off/feature-off.component';
import { ColourPickerComponent } from './components/colour-picker/colour-picker.component';
import {ColorPickerModule} from "ngx-color-picker";
import { ThemeContentComponent } from './components/theme-content/theme-content.component';
import { CronExpressionComponent } from './components/cron-expression/cron-expression.component';
import { CronExpressionInputComponent } from './components/cron-expression-input/cron-expression-input.component';
import { ElapsedTimeComponent } from './components/elapsed-time/elapsed-time.component';
import { KnockoutFormWrapperComponent } from './components/knockout-form-wrapper/knockout-form-wrapper.component';
import { EmbeddedReportComponent } from './components/embedded-report/embedded-report.component';
import {HighchartsChartModule} from "../highcharts/highcharts-chart.module";
import { ReportDashboardListComponent } from './components/report-dashboard-list/report-dashboard-list.component';
import { DeveloperMenuBarComponent } from './components/developer-menu-bar/developer-menu-bar.component';
import { TenancySelectionComponent } from './components/tenancy-selection/tenancy-selection.component';
import { ProgressDialogComponent } from './components/progress-dialog/progress-dialog.component';
import {DialogModule, TooltipAllModule} from "@syncfusion/ej2-angular-popups";
import {ProgressBarModule} from "@syncfusion/ej2-angular-progressbar";
import { UserProfileMenuListComponent } from './components/user-profile-menu-list/user-profile-menu-list.component';
import { AuditHistoryJournalFilterComponent } from './components/journal/audit-history-journal-filter/audit-history-journal-filter.component';
import {
    ButtonAllModule,
    CheckBoxAllModule, ChipListAllModule,
    RadioButtonAllModule,
    SwitchAllModule
} from "@syncfusion/ej2-angular-buttons";
import {
    ComboBoxAllModule,
    DropDownListAllModule,
    ListBoxAllModule,
    MultiSelectAllModule
} from "@syncfusion/ej2-angular-dropdowns";
import {DropDownButtonAllModule, SplitButtonAllModule} from "@syncfusion/ej2-angular-splitbuttons";
import { TableGridComponent } from './components/data-grids/table-grid/table-grid.component';
import { JsonViewerComponent } from './components/json-viewer/json-viewer.component';
import {NgxJsonViewerModule} from "ngx-json-viewer";
import {GridAllModule, PagerAllModule} from "@syncfusion/ej2-angular-grids";
import { EmailTextfieldRendererComponent } from "./components/field-renderer/email-textfield-renderer/email-textfield-renderer.component";
import { TextFieldRendererComponent } from './components/field-renderer/textfield-renderer/textfield-renderer.component';
import { BaseRendererComponent } from './components/field-renderer/base-renderer/base-renderer.component';
import { ChoiceRendererComponent } from './components/field-renderer/choice-renderer/choice-renderer.component';
import { SwitchRendererComponent } from './components/field-renderer/switch-renderer/switch-renderer.component';
import { DateRendererComponent } from './components/field-renderer/date-renderer/date-renderer.component';
import { OneToOneRendererComponent } from './components/field-renderer/onetoone-renderer/onetoone-renderer.component';
import { CheckboxRendererComponent } from './components/field-renderer/checkbox-renderer/checkbox-renderer.component';
import { MultiChoiceRendererComponent } from './components/field-renderer/multichoice-renderer/multichoice-renderer.component';
import { CodeRendererComponent } from './components/field-renderer/code-renderer/code-renderer.component';
import { CronRendererComponent } from './components/field-renderer/cron-renderer/cron-renderer.component';
import {DatePickerAllModule, DateTimePickerAllModule, TimePickerAllModule} from "@syncfusion/ej2-angular-calendars";
import { DateTimeRendererComponent } from './components/field-renderer/datetime-renderer/datetime-renderer.component';
import { TimeRendererComponent } from './components/field-renderer/time-renderer/time-renderer.component';
import { DurationRendererComponent } from './components/field-renderer/duration-renderer/duration-renderer.component';
import { WorkflowTransitionRendererComponent } from './components/field-renderer/workflowtransition-renderer/workflowtransition-renderer.component';
import { WorkflowStatusRendererComponent } from './components/field-renderer/workflowstatus-renderer/workflowstatus-renderer.component';
import { TableSelectorRendererComponent } from './components/field-renderer/tableselector-renderer/tableselector-renderer.component';
import { QueryFunctionSelectorRendererComponent } from './components/field-renderer/queryfunctionselector-renderer/queryfunctionselector-renderer.component';
import { I18ntextRendererComponent } from './components/field-renderer/i18ntext-renderer/i18ntext-renderer.component';
import { PasswordRendererComponent } from './components/field-renderer/password-renderer/password-renderer.component';
import { TextAreaRendererComponent } from './components/field-renderer/textarea-renderer/textarea-renderer.component';
import { HtmlRendererComponent } from './components/field-renderer/html-renderer/html-renderer.component';
import { ConditionRendererComponent } from './components/field-renderer/condition-renderer/condition-renderer.component';
import { TextWithLinkRendererComponent } from './components/field-renderer/textwithlink-renderer/textwithlink-renderer.component';
import { ListRendererComponent } from './components/field-renderer/list-renderer/list-renderer.component';
import { PropertiesRendererComponent } from './components/field-renderer/properties-renderer/properties-renderer.component';
import { ColourPickerRendererComponent } from './components/field-renderer/colourpicker-renderer/colourpicker-renderer.component';
import { AccountingRendererComponent } from './components/field-renderer/accounting-renderer/accounting-renderer.component';
import { PercentageRendererComponent } from './components/field-renderer/percentage-renderer/percentage-renderer.component';
import { IconRendererComponent } from './components/field-renderer/icon-renderer/icon-renderer.component';
import { ReferenceRendererComponent } from './components/field-renderer/reference-renderer/reference-renderer.component';
import { RelativeTimeRendererComponent } from './components/field-renderer/relativetime-renderer/relativetime-renderer.component';
import { OneToManyRendererComponent } from './components/field-renderer/onetomany-renderer/onetomany-renderer.component';
import { GenericReferenceRendererComponent } from './components/field-renderer/genericreference-renderer/genericreference-renderer.component';
import { AttachmentRendererComponent } from './components/field-renderer/attachment-renderer/attachment-renderer.component';
import { FileUploadRendererComponent } from './components/field-renderer/fileupload-renderer/fileupload-renderer.component';
import { JournalRendererComponent } from './components/field-renderer/journal-renderer/journal-renderer.component';
import { ActivityRendererComponent } from './components/field-renderer/activity-renderer/activity-renderer.component';
import {
    AccordionAllModule,
    ContextMenuAllModule,
    TabAllModule,
    ToolbarAllModule
} from "@syncfusion/ej2-angular-navigations";
import { HomeComponent } from './components/home/home.component';
import { ImportTransformDiagramComponent } from './components/import-transform-diagram/import-transform-diagram.component';
import {ListViewAllModule} from "@syncfusion/ej2-angular-lists";
import { JournalAttachmentSelectorComponent } from './components/journal/journal-attachment-selector/journal-attachment-selector.component';
import { ServerScriptMessagesComponent } from './components/data-grids/server-script-messages/server-script-messages.component';
import { RtfEditorComponent } from './components/rtf-editor/rtf-editor.component';
import {RichTextEditorAllModule} from "@syncfusion/ej2-angular-richtexteditor";
import { DetailTooltipComponent } from './components/detail-tooltip/detail-tooltip.component';
import { VersionTooltipComponent } from './components/version-tooltip/version-tooltip.component';
import { SystemJournalComponent } from './components/journal/system-journal/system-journal.component';
import {EditorModule} from '@tinymce/tinymce-angular';
import { SystemJournalEmailComponent } from './components/journal/system-journal-email/system-journal-email.component';
import {TextBoxAllModule, UploaderAllModule} from "@syncfusion/ej2-angular-inputs";
import { KnockoutNuclearFieldComponent } from './components/knockout-nuclear-field/knockout-nuclear-field.component';
import { OpenAIChatComponent } from './components/openai-chat/openai-chat.component';
import { LocaleSelectorComponent } from './components/locale-selector/locale-selector.component';
import { LocaleHtmlConfigurationComponent } from './components/locale-html-configuration/locale-html-configuration.component';

import { ModalWrapperComponent } from './components/modal-wrapper/modal-wrapper.component';
import { QueryFunctionsComponent } from './components/query-functions/query-functions.component';
import { KanbanBoardComponent } from './components/kanban-board/kanban-board.component';
import { KanbanModule } from "@syncfusion/ej2-angular-kanban";
import { SiteHeaderComponent } from './components/site-header/site-header.component';

import {SplitterAllModule} from "@syncfusion/ej2-angular-layouts";
import {CodemirrorModule} from "@ctrl/ngx-codemirror";

import { UserAvatarComponent } from "../user-avatar/user-avatar.component";
import { SearchBarComponent } from './components/search-bar/search-bar.component';
import {QueryFiltersComponent} from "./components/query-filters/query-filters.component";
import {ToggleComponent} from "./components/toggle/toggle.component";
import { ImageLightboxDirective } from './directives/image-lightbox/image-lightbox.directive';
import { ImageLightboxComponent } from './components/image-lightbox/image-lightbox.component';
import {ImpersonateDialogComponent} from './components/impersonate-dialog/impersonate-dialog.component';
import { SavedFiltersComponent } from './components/saved-filters/saved-filters.component';


import { ExpandoComponent } from './components/expando/expando.component';
import { KanbanMemberSelectorComponent } from './components/kanban-board/kanban-member-selector/kanban-member-selector.component';
import { KanbanAddMemberDialogComponent } from './components/kanban-board/kanban-add-member-dialog/kanban-add-member-dialog.component';
import { SystemJournalWhatsappComponent } from './components/journal/system-journal-whatsapp/system-journal-whatsapp.component';
import { FeatureFlagResetComponent } from './components/feature-flag-reset/feature-flag-reset.component';
import {
  KanbanDueDatePickerComponent
} from "./components/kanban-board/kanban-due-date-picker/kanban-due-date-picker.component";

@NgModule({
    imports: [
        CommonModule,
        SharedModule,
        MonacoEditorModule,
        ColorPickerModule,
        // DragDropModule,
        HighchartsChartModule,
        DialogModule,
        ProgressBarModule,
        ButtonAllModule,
        MultiSelectAllModule,
        ListBoxAllModule,
        SplitButtonAllModule,
        NgxJsonViewerModule,
        GridAllModule,
        RadioButtonAllModule,
        CheckBoxAllModule,
        DatePickerAllModule,
        DateTimePickerAllModule,
        TimePickerAllModule,
        ToolbarAllModule,
        PagerAllModule,
        DropDownButtonAllModule,
        ListViewAllModule,
        ContextMenuAllModule,
        RichTextEditorAllModule,
        TooltipAllModule,
        EditorModule,
        ComboBoxAllModule,
        TextBoxAllModule,
        TabAllModule,
        SwitchAllModule,
        DropDownListAllModule,
        ChipListAllModule,
        SplitterAllModule,
        CodemirrorModule,
        KanbanModule,
        AccordionAllModule,
        UploaderAllModule,
    ],
    providers: [
        SettingsInitializer,
        WindowPropertyInitializer,
        LegacyInitializer,
        PortalBaseComponent,
        KnockoutFieldComponent,
        FeatureFlagsInitializer,
        FeatureFlagComponent,
        FeatureFlagListComponent,
        FeatureFlagCanLoadGuard,
        FeatureFlagCanActivateGuard,
        FeatureFlagCanActivateChildGuard,
        FeatureOnComponent,
        FeatureOffComponent,
        CronExpressionComponent,
        FieldService
    ],
    exports: [
        KnockoutFieldComponent,
        MonacoEditorComponent,
        CronExpressionComponent,
        CronExpressionInputComponent,
        ProgressDialogComponent,
        AuditHistoryJournalFilterComponent,
        JournalAttachmentSelectorComponent,
        SystemJournalComponent,
        SystemJournalEmailComponent,
        RtfEditorComponent,
        OpenAIChatComponent,
        EmbeddedReportComponent,
        TextFieldRendererComponent
    ],
    declarations: [
        PortalBaseComponent,
        KnockoutFieldComponent,
        MonacoEditorComponent,
        FeatureFlagComponent,
        FeatureFlagListComponent,
        FeatureOnDirective,
        FeatureOffDirective,
        FeatureOnComponent,
        FeatureOffComponent,
        ColourPickerComponent,
        ThemeContentComponent,
        CronExpressionComponent,
        CronExpressionInputComponent,
        ElapsedTimeComponent,
        KnockoutFormWrapperComponent,
        EmbeddedReportComponent,
        ReportDashboardListComponent,
        DeveloperMenuBarComponent,
        TenancySelectionComponent,
        ProgressDialogComponent,
        UserProfileMenuListComponent,
        AuditHistoryJournalFilterComponent,
        TableGridComponent,
        JsonViewerComponent,
        TextFieldRendererComponent,
        BaseRendererComponent,
        ChoiceRendererComponent,
        SwitchRendererComponent,
        DateRendererComponent,
        OneToOneRendererComponent,
        CheckboxRendererComponent,
        MultiChoiceRendererComponent,
        CodeRendererComponent,
        CronRendererComponent,
        DateTimeRendererComponent,
        TimeRendererComponent,
        DurationRendererComponent,
        WorkflowTransitionRendererComponent,
        WorkflowStatusRendererComponent,
        TableSelectorRendererComponent,
        QueryFunctionSelectorRendererComponent,
        I18ntextRendererComponent,
        PasswordRendererComponent,
        TextAreaRendererComponent,
        HtmlRendererComponent,
        ConditionRendererComponent,
        TextWithLinkRendererComponent,
        ListRendererComponent,
        PropertiesRendererComponent,
        ColourPickerRendererComponent,
        AccountingRendererComponent,
        PercentageRendererComponent,
        IconRendererComponent,
        ReferenceRendererComponent,
        RelativeTimeRendererComponent,
        OneToManyRendererComponent,
        GenericReferenceRendererComponent,
        AttachmentRendererComponent,
        FileUploadRendererComponent,
        JournalRendererComponent,
        ActivityRendererComponent,
        HomeComponent,
        ImportTransformDiagramComponent,
        JournalAttachmentSelectorComponent,
        ServerScriptMessagesComponent,
        RtfEditorComponent,
        DetailTooltipComponent,
        VersionTooltipComponent,
        SystemJournalComponent,
        SystemJournalEmailComponent,
        KnockoutNuclearFieldComponent,
        OpenAIChatComponent,
        LocaleSelectorComponent,
        LocaleHtmlConfigurationComponent,
        ModalWrapperComponent,
        QueryFunctionsComponent,
        SiteHeaderComponent,
        KanbanBoardComponent,
        UserAvatarComponent,
        SearchBarComponent,
        QueryFiltersComponent,
        ToggleComponent,
        ImageLightboxDirective,
        ImageLightboxComponent,
        ImpersonateDialogComponent,
        SavedFiltersComponent,
        ExpandoComponent,
        KanbanMemberSelectorComponent,
        KanbanAddMemberDialogComponent,
        KanbanDueDatePickerComponent,
        SystemJournalWhatsappComponent,
        FeatureFlagResetComponent,
        EmailTextfieldRendererComponent
    ],
    schemas: [ CUSTOM_ELEMENTS_SCHEMA ]
})
export class CoreModule {
    constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    }
}
